/**
 * Глобальный сервис для управления всеми звуками приложения
 * Предзагружает все звуки при старте приложения для мгновенного воспроизведения
 */

class AudioService {
  constructor() {
    this.audioContext = null
    this.audioBuffers = {}
    this.activeSources = [] // Массив активных источников звука
    this.isInitialized = false
    this.isLoading = false
    
    // Конфигурация всех звуков приложения
    this.soundsConfig = {
      // Кнопки
      buttonClick: { url: '/sounds/common/main-button-click.mp3', volume: 0.5 },
      hover: { url: '/sounds/common/hover.mp3', volume: 0.7 },
      pick: { url: '/sounds/common/pick.mp3', volume: 0.7 },
      select: { url: '/sounds/common/select.mp3', volume: 0.7 },
      
      // Открытие кейсов
      caseSingleGoodItem: { url: '/sounds/cases/case-single-good-item.mp3', volume: 0.9 },
      caseMultiStart: { url: '/sounds/multicase/Opening_Cases_waiting_screen_V2.mp3', volume: 0.9 },
      caseMultiWin: { url: '/sounds/multicase/Case_Opened.mp3', volume: 0.9 },
      caseMultiWinGood: { url: '/sounds/multicase/Case_Opened_with_Confetti.mp3', volume: 0.9 },
      caseMultiFinal: { url: '/sounds/multicase/case-multi-final.mp3', volume: 0.7 },
      
      // Рулетка
      rouletteSpin: { url: '/sounds/cases/v3_Roulette_Spin.mp3', volume: 0.9 },
      
      // Апгрейдер
      upgraderSpin: { url: '/sounds/upgrader/Upgrader_Roulette_Spin_V4.mp3', volume: 0.9 },
      upgraderWin: { url: '/sounds/upgrader/Upgrader_Win_Successful_Upgrade_V1.mp3', volume: 0.9 },
      upgraderFail: { url: '/sounds/upgrader/Upgrader_Failed_result_V1.mp3', volume: 0.9 },
      
      // Таймеры
      timer: { url: '/sounds/common/v4_timer_3-2-1-0.mp3', volume: 0.9 },
      
      // Выигрыши
      winV1: { url: '/sounds/cases/v1_BigWin+pulse.mp3', volume: 0.9 },
      winV3: { url: '/sounds/cases/v3_win+pulse.mp3', volume: 0.9 },
      win: { url: '/sounds/cases/win.mp3', volume: 0.3 },
      
      // Контракты
      contractSign: { url: '/sounds/contracts/Loading_SigningContract.mp3', volume: 0.9 },
      contractWin: { url: '/sounds/contracts/Win_ContractSigned.mp3', volume: 0.9 },
      
      // Продажа и покупка
      sell: { url: '/sounds/common/sell-item.mp3', volume: 0.9 },
      buyItem: { url: '/sounds/common/buy-item.mp3', volume: 0.3 },
      successPayment: { url: '/sounds/common/success-payment.mp3', volume: 0.9 },
      
      // Кейс-баттлы
      userJoinBattle: { url: '/sounds/case-battle/user_join_battle.mp3', volume: 0.9 },
      battleRouletteStart: { url: '/sounds/case-battle/start_roulette.mp3', volume: 0.9 },
      caseBattleRouletteStart: { url: '/sounds/case-battle/Roulette_V1.mp3', volume: 0.9 },
      caseBattleWin: { url: '/sounds/case-battle/Win_Case_V1.mp3', volume: 0.9 },
      caseBattleLost: { url: '/sounds/case-battle/Lost_Case_V1.mp3', volume: 0.9 },
      caseBattleItemAppear1: { url: '/sounds/case-battle/Item_Appear_1_V2.mp3', volume: 0.9 },
      caseBattleItemAppear2: { url: '/sounds/case-battle/Item_Appear_2_V2.mp3', volume: 0.9 },
      caseBattleItemAppear3: { url: '/sounds/case-battle/Item_Appear_3_V2.mp3', volume: 0.9 },
      caseBattleItemAppear4: { url: '/sounds/case-battle/Item_Appear_4_V2.mp3', volume: 0.9 },

      // Double
      doubleWin: { url: '/sounds/double/win_V2.mp3', volume: 0.9 },
      doubleTimerStartEnd: { url: '/sounds/double/timer_start+end._V1.mp3', volume: 0.9 },
      doubleSpinReel: { url: '/sounds/double/spin_reel_V1.mp3', volume: 0.9 },
      doubleBetAccepted: { url: '/sounds/double/bet_accepted_icon_V2.mp3', volume: 0.9 },

      // Royale (Королевская битва)
      royaleBetAccepted: { url: '/sounds/royale/Bet_Accepted_Player_icon_V2.mp3', volume: 0.9 },
      royaleRouletteSpin: { url: '/sounds/royale/Roulette_Spin_V2.mp3', volume: 0.9 },
      royaleTimerLastSeconds: { url: '/sounds/royale/Timer_Count_Last_Seconds_V1_3-2-1-0.mp3', volume: 0.9 },
      royaleTimerPressure: { url: '/sounds/royale/Timer_Pressure_Sweep_V1-start_on-45sec.mp3', volume: 0.8 },
      royaleWin: { url: '/sounds/royale/Win_V2.mp3', volume: 0.9 }
    }
  }

  /**
   * Инициализация AudioContext и предзагрузка всех звуков
   */
  async initialize() {
    if (this.isInitialized || this.isLoading) {
      console.log('⏭️ AudioService already initialized or loading')
      return
    }

    this.isLoading = true
    console.log('🎵 Initializing AudioService...')

    try {
      // Создаем AudioContext
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
      console.log('✅ AudioContext created')

      // Загружаем все звуки параллельно
      const loadPromises = Object.entries(this.soundsConfig).map(([key, config]) =>
        this.loadSound(key, config.url)
      )

      await Promise.all(loadPromises)
      
      this.isInitialized = true
      this.isLoading = false
      console.log('✅ All sounds preloaded successfully!')
      console.log(`📊 Loaded ${Object.keys(this.audioBuffers).length} sounds`)
    } catch (error) {
      console.error('❌ Failed to initialize AudioService:', error)
      this.isLoading = false
    }
  }

  /**
   * Загрузка одного звука
   */
  async loadSound(key, url) {
    try {
      console.log(`🔊 Loading sound: ${key} from ${url}`)
      
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const arrayBuffer = await response.arrayBuffer()
      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        throw new Error(`Empty audio file: ${url}`)
      }
      
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer)
      this.audioBuffers[key] = audioBuffer
      
      console.log(`✅ Sound loaded: ${key} (duration: ${audioBuffer.duration.toFixed(2)}s)`)
    } catch (error) {
      console.error(`❌ Failed to load sound ${key}:`, error)
    }
  }

  /**
   * Воспроизведение звука
   * @param {string} key - Ключ звука из soundsConfig
   * @param {number} volumeOverride - Опциональная перезапись громкости (0.0 - 1.0)
   * @param {Object} options - Дополнительные опции воспроизведения
   * @param {boolean} options.loop - Зациклить воспроизведение
   */
  play(key, volumeOverride = null, options = {}) {
    try {
      // Проверяем инициализацию
      if (!this.audioContext || !this.isInitialized) {
        console.warn('⚠️ AudioService not initialized yet')
        return
      }

      // Проверяем наличие буфера
      if (!this.audioBuffers[key]) {
        console.warn(`⚠️ Sound buffer not found: ${key}`)
        return
      }

      // Возобновляем AudioContext если он был приостановлен (autoplay policy)
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume()
      }

      // Создаем новый source для каждого воспроизведения
      const source = this.audioContext.createBufferSource()
      source.buffer = this.audioBuffers[key]
      source.loop = Boolean(options?.loop)

      // Создаем GainNode для управления громкостью
      const gainNode = this.audioContext.createGain()
      
      // Используем переданную громкость или из конфигурации
      const volume = volumeOverride !== null 
        ? volumeOverride 
        : (this.soundsConfig[key]?.volume || 1.0)
      
      gainNode.gain.value = volume

      // Подключаем цепочку: source -> gain -> destination
      source.connect(gainNode)
      gainNode.connect(this.audioContext.destination)

      // Добавляем source в массив активных
      this.activeSources.push({ key, source, gainNode })

      // Воспроизводим звук
      source.start(0)

      // Автоматически очищаем source после завершения
      source.onended = () => {
        source.disconnect()
        gainNode.disconnect()
        // Удаляем из активных источников
        this.activeSources = this.activeSources.filter(s => s.source !== source)
      }

      console.log(`🔊 Playing sound: ${key} at volume ${volume}${source.loop ? ' (loop)' : ''}`)
    } catch (error) {
      console.error(`❌ Failed to play sound ${key}:`, error)
    }
  }

  /**
   * Остановка конкретного звука по ключу
   * @param {string} key - Ключ звука для остановки
   */
  stop(key) {
    try {
      // Находим все активные источники с данным ключом
      const sourcesToStop = this.activeSources.filter(s => s.key === key)
      
      sourcesToStop.forEach(({ source, gainNode }) => {
        try {
          // Плавное затухание звука (fade out)
          gainNode.gain.setValueAtTime(gainNode.gain.value, this.audioContext.currentTime)
          gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.1)
          
          // Останавливаем источник через 100ms (после затухания)
          setTimeout(() => {
            try {
              source.stop()
              source.disconnect()
              gainNode.disconnect()
            } catch (e) {
              // Источник уже остановлен
            }
          }, 100)
        } catch (error) {
          console.warn(`⚠️ Failed to stop source for ${key}:`, error)
        }
      })
      
      // Удаляем остановленные источники из массива
      this.activeSources = this.activeSources.filter(s => s.key !== key)
      
      if (sourcesToStop.length > 0) {
        console.log(`⏹️ Stopped ${sourcesToStop.length} instance(s) of sound: ${key}`)
      }
    } catch (error) {
      console.error(`❌ Failed to stop sound ${key}:`, error)
    }
  }

  /**
   * Остановка всех звуков
   */
  stopAll() {
    try {
      // Останавливаем все активные источники
      this.activeSources.forEach(({ source, gainNode }) => {
        try {
          source.stop()
          source.disconnect()
          gainNode.disconnect()
        } catch (e) {
          // Источник уже остановлен
        }
      })
      
      // Очищаем массив активных источников
      this.activeSources = []
      
      console.log('⏹️ All sounds stopped')
    } catch (error) {
      console.error('❌ Failed to stop all sounds:', error)
    }
  }

  /**
   * Получение статуса загрузки
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      isLoading: this.isLoading,
      loadedSounds: Object.keys(this.audioBuffers).length,
      totalSounds: Object.keys(this.soundsConfig).length
    }
  }
}

// Создаем единственный экземпляр (singleton)
const audioService = new AudioService()

export default audioService
